import { isNil } from 'lodash'
import moment from 'moment'

import helpers from 'helpers'

import constants from 'constants/index'
import * as yup from 'yup'

const { EVIDENCE_FILE_TYPES, EVIDENCE_FILE_MIMES_TYPES } = constants.planAction

const currentDay = new Date(new Date().setDate(new Date().getDate() - 1))

const planActionsSchema = ({
  minDate,
  maxDate,
  activityDeadlineDate,
  planActionsOpened,
}) =>
  yup.object().shape({
    id: yup.number(),
    originId: yup.number(),
    status: yup.string(),
    responsible: yup
      .object()
      .shape({
        id: yup.number().nullable(),
        name: yup.string(),
      })
      .nullable()
      .notRequired()
      .typeError('Selecione uma opção válida'),
    deadlineDate: yup
      .date()
      .typeError('Precisa ser uma data válida')
      .when(['originId'], {
        is: (originId) =>
          planActionsOpened.includes(originId) || isNil(originId),
        then: (schema) =>
          schema
            .min(
              moment.max(
                moment(currentDay),
                moment(minDate).subtract(1, 'day'),
              ),
              `O prazo não pode ser anterior à data de hoje nem à data inicial do projeto, ${helpers.formatters.date(
                minDate,
              )}`,
            )
            .max(
              moment(activityDeadlineDate).isValid()
                ? moment.min(moment(maxDate), moment(activityDeadlineDate))
                : maxDate,
              `O prazo não pode ser posterior à data final da atividade ou do projeto, ${helpers.formatters.date(
                moment(activityDeadlineDate).isValid()
                  ? moment.min(moment(maxDate), moment(activityDeadlineDate))
                  : maxDate,
              )}`,
            ),
      }),
    what: yup.string().max(5000),
    how: yup.string().max(5000),
    because: yup.string().max(5000),
    where: yup.string().max(255),
    howPrice: yup.string().max(18),
  })

const commonEvidenceFields = {
  justify: yup.string().max(5000),
  attachments: yup.mixed().fileUpload({
    sizeLimit: 30,
    unit: 'MB',
    types: EVIDENCE_FILE_TYPES,
    mimeTypes: EVIDENCE_FILE_MIMES_TYPES,
  }),
  privacyPolicyIds: yup.array().of(
    yup.object().shape({
      id: yup.number(),
      name: yup.string(),
    }),
  ),
}

// Evidência exigida pelo template: nome e descrição vêm prontos e são somente leitura na tela, então
// não há o que cobrar do usuário.
const evidenceSchema = yup.object().shape({
  title: yup.string(),
  description: yup.string().max(5000),
  ...commonEvidenceFields,
})

// Evidência criada manualmente: nome e descrição são digitados pelo usuário. Sem obrigá-los aqui a
// evidência nascia sem nome e sumia das telas que a exibem pelo título (controles do documento, por
// exemplo) — o backend recusa o mesmo caso, esta validação só evita a ida até lá.
const manualEvidenceSchema = yup.object().shape({
  title: yup.string().required(),
  description: yup.string().max(5000).required(),
  ...commonEvidenceFields,
})

const schema = ({
  minDate,
  maxDate,
  planActionsOpened,
  activityDeadlineDate,
  selectedAlternative,
}) =>
  yup.object().shape({
    responsible: yup
      .object()
      .shape({
        id: yup.number().nullable(),
        name: yup.string(),
      })
      .nullable()
      .notRequired()
      .typeError('Selecione uma opção válida'),
    deadlineDate: yup
      .date()
      .required()
      .typeError('Precisa ser uma data válida')
      .min(
        moment.max(moment(currentDay), moment(minDate).subtract(1, 'day')),
        `O prazo não pode ser anterior à data de hoje nem à data inicial do projeto, ${helpers.formatters.date(
          minDate,
        )}`,
      )
      .max(
        maxDate,
        `O prazo não pode ser posterior à data final do projeto, ${helpers.formatters.date(
          maxDate,
        )}`,
      ),
    alternativeAnswerId: yup.string(),
    observations: yup
      .string()
      .max(5000)
      .when([], {
        is: () => selectedAlternative?.observationsRequired,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
      }),
    evidencePlanActions: yup.array().of(
      yup.object().shape({
        id: yup.number(),
        originId: yup.number(),
        createdManually: yup.boolean(),
        evidence: yup.object().when('createdManually', {
          is: true,
          then: manualEvidenceSchema,
          otherwise: evidenceSchema,
        }),
        planActions: yup.array().of(
          planActionsSchema({
            minDate,
            maxDate,
            planActionsOpened,
            activityDeadlineDate,
          }),
        ),
      }),
    ),
  })

export default schema
